import * as THREE from 'three';
import { eventBus } from '../../../eventBus';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';
import { safeResource } from '@/engine/Utilities/IDisposable';
import type { SliceOrientationsConfig } from './interfaces';
import { SliceType } from './interfaces';
import { isMobile, isAndroid } from 'react-device-detect';
import type { XRayEffect } from './ImageStack/ImageStack';
import { ImageStack } from './ImageStack/ImageStack';
import { ImageStackUrlResolver } from './ImageStack/ImageStackUrlResolver';
import type { ImageStackSlicer } from './ImageStack/ImageStackSlicer';
import type { ImageSlice } from './ImageStack/ImageSlice';
import { ImageSliceCanvasRenderingContext } from './ImageStack/ImageSliceCanvasRenderingContext';
import { makeTransformation } from '@/engine/PlanningAlgorithm/Helpers';
import { makeRotationMatrix } from '@/engine/utils/geometryHelper';
import type { INotificationsCenter } from '../../interfaces';
export type SlicesInitializer = (imageStackController: ImageStackController) => void;

export type OnSynchronizationPointChangedListener = (synchronizationPoint: THREE.Vector3) => void;
export type OnReadyStateChangedListener = (isReady: boolean) => void;
export type OnErrorListener = () => void;
export type OnLoadingProgressListener = (isReduced: boolean, framesLoaded: number, framesTotal: number) => void;
export type SliceIndexSetter = (index: number) => void;
export type SliceByPointSetter = (point: THREE.Vector3) => void;

export interface MinMax {
  min: number;
  max: number;
}

export interface ContrastPoint {
  from: number;
  to: number;
}

export interface ContrastFunction {
  lowerBound: ContrastPoint;
  upperBound: ContrastPoint;
}

export interface MedicalImage {
  contrast?: ContrastFunction;
  ids: Array<string>;
}

export interface ImageUrls {
  imageDataHeaderUrl: string;
  imageDataUrl: string;
  reducedImageDataHeaderUrl: string;
  reducedImageDataUrl: string;
}

export interface RawBuffer {
  size: number;
  ptr: number;
}

export interface VolumeOrientation {
  left: string;
  right: string;
  top: string;
  bottom: string;
  front: string;
  back: string;
}

export interface SlicingContext {
  xRaySlicerParams?: XRaySlicerParams;
  imageSlicer: ImageStackSlicer;
  imageSlice?: ImageSlice;
  imageReductionFactor: number;
  sliceReductionFactor: number;
}

export interface SliceOrientationInfo {
  canvas: HTMLCanvasElement;
  xRaySlicerParams?: XRaySlicerParams;
  sliceThickness: number;
  slicesCount: number;
  volumeCS: THREE.Matrix4;
  volumeOrientation: VolumeOrientation;
  volumeDimensions: THREE.Vector3;
  voxelDimensions: THREE.Vector3;
  imageReductionFactor: number;
  isReduced: boolean;
  deltaTransformation?: THREE.Matrix4;
}

export interface SlicePositionInfo {
  xRayEffect?: XRayEffect;
  sliceThickness: number;
  sliceIndex: number;
  slicePosition: number;
  mprSliceRotations: Map<SliceType, number>;
  sliceCS: THREE.Matrix4;
  sliceCSInv: THREE.Matrix4;
}

export interface SliceVisualizationInfo {
  reductionFactorFromOriginal: number; // SlicerContext.imageReductionFactor * SlicerContext.sliceReductionFactor,
  contrastWindowWidth: number;
  contrastWindowLevel: number;
}

export interface XRaySlicerParams {
  xRayEffect: XRayEffect;
  xRaySliceThickness?: number;
  xRayRescale?: number;
  xRayMaxSlicesCount?: number;
  xRayReductionFactor?: number;
}

export type OnOrientationChangedListener = (orientationInfo: SliceOrientationInfo) => void;
export type OnPositionChangedListener = (positionInfo: SlicePositionInfo) => void;
export type OnVisualizationChangedListener = (visualizationInfo: SliceVisualizationInfo) => void;

export interface SliceData {
  renderingContext: ImageSliceCanvasRenderingContext;
  slicingContextRef?: SlicingContext;
  orientationInfo?: SliceOrientationInfo;
  positionInfo?: SlicePositionInfo;
  visualizationInfo?: SliceVisualizationInfo;
  onOrientationChangedListeners: CallbackManager<OnOrientationChangedListener>;
  onPositionChangedListeners: CallbackManager<OnPositionChangedListener>;
  onVisualizationChangedListeners: CallbackManager<OnVisualizationChangedListener>;
}

export function zeroSlicesInitializer(imageStackController: ImageStackController): void {
  for (const sliceType of imageStackController.getRegisteredSliceTypes()) {
    imageStackController.requestSliceByIndex(sliceType, 0);
  }
}

export function middleIndexSlicesInitializer(imageStackController: ImageStackController): void {
  for (const sliceType of imageStackController.getRegisteredSliceTypes()) {
    const orientationInfo = imageStackController.getSliceOrientationInfo(sliceType);
    if (!orientationInfo) {
      throw new Error('Slice orientationInfo is undefined');
    }
    const index = Math.round((orientationInfo.slicesCount - 1) / 2);
    imageStackController.requestSliceByIndex(sliceType, index);
  }
}

export function centerPointSlicesInitializer(imageStackController: ImageStackController): void {
  for (const sliceType of imageStackController.getRegisteredSliceTypes()) {
    const orientationInfo = imageStackController.getSliceOrientationInfo(sliceType);
    if (!orientationInfo) {
      throw new Error('Slice orientationInfo is undefined');
    }
    const vecZ = new THREE.Vector3();
    orientationInfo.volumeCS.extractBasis(new THREE.Vector3(), new THREE.Vector3(), vecZ);
    const origin = new THREE.Vector3().setFromMatrixPosition(orientationInfo.volumeCS);
    const zShift = ((orientationInfo.slicesCount - 1) / 2) * orientationInfo.voxelDimensions.z;
    const center = origin.add(vecZ.multiplyScalar(zShift));
    imageStackController.requestSliceByPoint(sliceType, center);
  }
}

type _ComplimentarySliceAxisInfo = Map<SliceType, [number, boolean]>;

export class ImageStackController {
  private readonly _medicalImage: MedicalImage;

  private readonly _isMultiPhase: boolean;

  private readonly _slicesInitializer: SlicesInitializer;

  private readonly _sliceOrientationsConfig: SliceOrientationsConfig;

  private readonly _sliceTypes: Map<SliceType, boolean> = new Map();

  private readonly _mainSliceTypes: Array<SliceType> = [];

  private readonly _imageStackContexts: Map<
    string,
    { imageStack: ImageStack; slicingContexts: Map<SliceType, SlicingContext> }
  >;

  private _activeImageStackId?: string;

  private _isCompleted = false;

  private _isCineloopAvailable = false;

  private _contrastWindowDef: MinMax = { min: 0, max: 4095 };

  private _contrastWindow: MinMax;

  private readonly _data: Map<SliceType, SliceData> = new Map();

  private readonly _onSynchronizationPointChangedListeners =
    new CallbackManager<OnSynchronizationPointChangedListener>();

  private readonly _onReadyStateChangedListeners = new CallbackManager<OnReadyStateChangedListener>();

  private readonly _onErrorListeners = new CallbackManager<OnErrorListener>();

  private readonly _onLoadingProgressListeners = new CallbackManager<OnLoadingProgressListener>();

  private readonly _notificationsCenter?: INotificationsCenter;

  private _initialSynchronizationPoint?: THREE.Vector3;

  private _synchronizationPoint?: THREE.Vector3;

  private _orientationCSs?: Map<SliceType, THREE.Matrix4>;

  private _slicePositions?: Map<SliceType, THREE.Vector3>;

  private _initialOrientationCSs?: Map<SliceType, THREE.Matrix4>;

  private _complimentarySlicesInfos?: Map<SliceType, _ComplimentarySliceAxisInfo>;

  private _xRaySlicerParams = new Map<SliceType, XRaySlicerParams>();

  private _shouldCompensateObliqueness = true; // MPR for oblique images: 2D view is in Image(true) or World(false) CS

  private _isReady = false;

  private _isError = false;

  private _initializationJob?: () => void;

  public constructor(
    medicalImage: MedicalImage,
    urlResolver: (uriGuid: string) => Promise<string>,
    sliceOrientationsConfig: SliceOrientationsConfig,
    slicesInitializer: SlicesInitializer = zeroSlicesInitializer,
    notificationsCenter?: INotificationsCenter
  ) {
    this._medicalImage = medicalImage;
    this._isMultiPhase = this._medicalImage.ids.length > 1;
    this._sliceOrientationsConfig = sliceOrientationsConfig;
    this._slicesInitializer = slicesInitializer;
    this._notificationsCenter = notificationsCenter;

    if (this._medicalImage.contrast) {
      this._contrastWindowDef.min = this._medicalImage.contrast.lowerBound.from;
      this._contrastWindowDef.max = this._medicalImage.contrast.upperBound.from;
      if (this._medicalImage.contrast.lowerBound.to != 0 || this._medicalImage.contrast.upperBound.to != 255) {
        console.warn('Medical image has narrow default output contrast window. Ignored.');
      }
    }

    this._contrastWindow = {
      min: this._contrastWindowDef.min,
      max: this._contrastWindowDef.max
    };

    this._imageStackContexts = new Map();
    for (const id of this._medicalImage.ids) {
      this._imageStackContexts.set(id, {
        imageStack: new ImageStack(id, new ImageStackUrlResolver(urlResolver), this._contrastWindowDef),
        slicingContexts: new Map()
      });
    }

    for (const sliceType of this._sliceOrientationsConfig.keys()) {
      //const isMain =
      //  sliceType === SliceType.AXIAL ||
      //  sliceType === SliceType.CORONAL ||
      //  sliceType === SliceType.SAGITTAL;
      //this._sliceTypes.set(sliceType, isMain);
      //if (isMain) {
      //  this._mainSliceTypes.push(sliceType);
      this._sliceTypes.set(sliceType, true);
      this._mainSliceTypes.push(sliceType);
    }
    //this._sliceTypes.set(SliceType.CUSTOM, false); //TODO: temporary

    for (const sliceType of this._sliceTypes.keys()) {
      this._data.set(sliceType, {
        renderingContext: new ImageSliceCanvasRenderingContext(),
        onOrientationChangedListeners: new CallbackManager<OnOrientationChangedListener>(),
        onPositionChangedListeners: new CallbackManager<OnPositionChangedListener>(),
        onVisualizationChangedListeners: new CallbackManager<OnVisualizationChangedListener>()
      });
    }
  }

  private _getImageStack(): ImageStack {
    const ret = this._activeImageStackId
      ? this._imageStackContexts.get(this._activeImageStackId)?.imageStack
      : undefined;
    if (!ret) {
      throw new Error('Logic Error');
    }
    return ret;
  }

  private _getSlicingContexts(): Map<SliceType, SlicingContext> {
    const ret = this._activeImageStackId
      ? this._imageStackContexts.get(this._activeImageStackId)?.slicingContexts
      : undefined;
    if (!ret) {
      throw new Error('Logic Error');
    }
    return ret;
  }

  private _getSlicingCS(orientation: string): THREE.Matrix4 {
    if (orientation.length != 3) {
      throw new Error('ImageStack: Invalid orientation');
    }
    const v = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    for (let i = 0; i < 3; ++i) {
      switch (orientation[i]) {
        case 'R':
          v[i].set(1, 0, 0);
          break;
        case 'L':
          v[i].set(-1, 0, 0);
          break;
        case 'A':
          v[i].set(0, -1, 0);
          break;
        case 'P':
          v[i].set(0, 1, 0);
          break;
        case 'T':
          v[i].set(0, 0, -1);
          break;
        case 'B':
          v[i].set(0, 0, 1);
          break;
        default:
          throw new Error('ImageStack: Unknown orientation');
      }
    }
    return makeTransformation(new THREE.Vector3(), v[0], v[1], v[2]);
  }

  //-------------------- Privates ------------------------
  private _getScanDefaultOrientationCS(sliceType: SliceType, applyDefaultMpr: boolean): THREE.Matrix4 {
    let orientation = this._sliceOrientationsConfig.get(sliceType);
    if (!orientation) {
      orientation = this._sliceOrientationsConfig.get(SliceType.AXIAL);
      if (!orientation) {
        throw new Error('Slice Orientation is undefined');
      }
    }
    const imageStack = this._getImageStack();
    const orientationCS = this._getSlicingCS(orientation);
    if (applyDefaultMpr) {
      const imageStackCS = imageStack.getCS();
      orientationCS.premultiply(imageStackCS);
    }
    return orientationCS;
  }

  private _getComplimentarySliceAxisInfo(sliceType: SliceType): _ComplimentarySliceAxisInfo {
    // The goal is to find the such slices for which Z-xis of provided slice
    //   is either vecX or vecY

    if (!this._initialOrientationCSs) {
      throw new Error('Initial Orientations expected to be valid');
    }

    const initialOrientationCs = this._initialOrientationCSs.get(sliceType);
    if (!initialOrientationCs) {
      throw new Error('orientationCS expected to be valid');
    }
    const initialVecZ = new THREE.Vector3().setFromMatrixColumn(initialOrientationCs, 2);

    const ret: _ComplimentarySliceAxisInfo = new Map();
    for (const [st, cs] of this._initialOrientationCSs) {
      if (st == sliceType) {
        continue;
      }
      for (let i = 0; i < 2; ++i) {
        const vec = new THREE.Vector3().setFromMatrixColumn(cs, i);
        const angleRad = initialVecZ.angleTo(vec);
        if (angleRad < 0.01) {
          ret.set(st, [i, true]);
        }
        if (Math.PI - angleRad < 0.01) {
          ret.set(st, [i, false]);
        }
      }
    }

    if (ret.size == 0) {
      throw new Error('Complimentary slice type not found');
    }

    return ret;
  }

  private _getComplimentarySlicesInfos(): Map<SliceType, _ComplimentarySliceAxisInfo> {
    const ret: Map<SliceType, _ComplimentarySliceAxisInfo> = new Map();
    for (const _sliceType of this._mainSliceTypes) {
      ret.set(_sliceType, this._getComplimentarySliceAxisInfo(_sliceType));
    }
    return ret;
  }

  private _getMprSliceRotation(
    sliceType: SliceType,
    complimentarySliceType: SliceType,
    axisInfo: [number, boolean]
  ): number {
    const orientationCS = this._orientationCSs?.get(sliceType);
    const complimentaryOrientationCS = this._orientationCSs?.get(complimentarySliceType);
    if (!orientationCS || !complimentaryOrientationCS) {
      throw new Error('Orientation CSs expected to be valid');
    }

    const baseVec = new THREE.Vector3().setFromMatrixColumn(orientationCS, 2);
    const vecZ = new THREE.Vector3().setFromMatrixColumn(complimentaryOrientationCS, 2);
    const vec = new THREE.Vector3().setFromMatrixColumn(complimentaryOrientationCS, axisInfo[0]);
    if (!axisInfo[1]) vec.negate();

    let angleRad = baseVec.angleTo(vec);
    if (baseVec.clone().cross(vec).dot(vecZ) < 0) {
      angleRad = -angleRad;
    }

    return angleRad;
  }

  private _getMprSliceRotations(sliceType: SliceType): Map<SliceType, number> {
    const complimentarySliceInfos = this._complimentarySlicesInfos?.get(sliceType);
    if (!complimentarySliceInfos) {
      throw new Error('Complimentary slice is unknown');
    }

    const ret = new Map<SliceType, number>();
    for (const [_sliceType, _axisInfo] of complimentarySliceInfos) {
      const angleRad = this._getMprSliceRotation(sliceType, _sliceType, _axisInfo);
      ret.set(_sliceType, angleRad);
    }

    return ret;
  }

  private _buildSlicingContext(sliceType: SliceType, resetMultiStackContext = true): SlicingContext {
    const data = this._data.get(sliceType);

    if (!data) {
      throw new Error('Slice Data in not existent for sliceType: ' + sliceType);
    }

    const slicingContexts = this._getSlicingContexts();

    if (resetMultiStackContext) {
      for (const imageStackContext of this._imageStackContexts.values()) {
        const slicingContext = imageStackContext.slicingContexts.get(sliceType);
        if (slicingContext) {
          slicingContext.imageSlice?.dispose();
          slicingContext.imageSlice = undefined;
          slicingContext.imageSlicer.dispose();
          imageStackContext.slicingContexts.delete(sliceType);
        }
      }
    } else {
      const slicingContext = slicingContexts.get(sliceType);
      if (slicingContext) {
        slicingContext.imageSlice?.dispose();
        slicingContext.imageSlice = undefined;
        slicingContext.imageSlicer.dispose();
        slicingContexts.delete(sliceType);
      }
    }

    data.slicingContextRef = undefined;

    const orientationCS = this._orientationCSs?.get(sliceType);
    if (!orientationCS) {
      throw new Error('Slice Orientation CS is undefined');
    }

    const imageStack = this._getImageStack();

    const xRaySlicerParams = this._xRaySlicerParams.get(sliceType);
    let imageSlicer: ImageStackSlicer | undefined = undefined;
    if (xRaySlicerParams) {
      if (xRaySlicerParams.xRayReductionFactor === undefined) {
        imageSlicer = imageStack.getXRayImageSlicer(
          orientationCS,
          xRaySlicerParams.xRayEffect,
          xRaySlicerParams.xRaySliceThickness ?? 0.0,
          xRaySlicerParams.xRayRescale ?? 0.0,
          xRaySlicerParams.xRayMaxSlicesCount ?? 0.0
        );
      } else {
        imageSlicer = imageStack.getXRayImageSlicerReduced(
          orientationCS,
          xRaySlicerParams.xRayEffect,
          xRaySlicerParams.xRaySliceThickness ?? 0.0,
          xRaySlicerParams.xRayRescale ?? 0.0,
          xRaySlicerParams.xRayMaxSlicesCount ?? 0.0,
          xRaySlicerParams.xRayReductionFactor
        );
      }
    } else {
      imageSlicer = imageStack.getImageSlicer(orientationCS);
    }

    const size = imageSlicer.getSize();

    data.renderingContext.init(size.x, size.y);

    const newSlicingContext = {
      xRaySlicerParams: xRaySlicerParams,
      imageSlicer: imageSlicer,
      imageReductionFactor: imageStack.getReductionFactor(),
      sliceReductionFactor: imageSlicer.getSliceReductionFactor(),
      imageSlice: undefined
    };

    slicingContexts.set(sliceType, newSlicingContext);

    data.slicingContextRef = newSlicingContext;

    return newSlicingContext;
  }

  private _buildOrientationInfo(sliceType: SliceType): SliceOrientationInfo {
    const data = this._data.get(sliceType);

    if (!data?.slicingContextRef) {
      throw new Error('Slice slicerContext is not existent for sliceType: ' + sliceType);
    }

    const xRaySlicerParams = data.slicingContextRef.xRaySlicerParams;

    const imageSlicer = data.slicingContextRef.imageSlicer;

    let slicesCount = imageSlicer.getSlicesCount();
    const sliceThickness = imageSlicer.getSliceThickness();
    const sliceSizes = imageSlicer.getSize();
    const sliceSpacings = imageSlicer.getSpacings();

    if (xRaySlicerParams && xRaySlicerParams.xRaySliceThickness === undefined) {
      slicesCount = 1;
      sliceSizes.z = slicesCount;
      sliceSpacings.z = sliceThickness;
    }

    const orientationCS = this._orientationCSs?.get(sliceType);
    if (!orientationCS) {
      throw new Error('Unknown orientation');
    }

    const volumeCS = imageSlicer.getVolumeCS();

    const volumeOrientationPlain = imageSlicer.getVolumeOrientation();

    const volumeOrientation: VolumeOrientation = {
      left: volumeOrientationPlain[0],
      right: volumeOrientationPlain[3],
      top: volumeOrientationPlain[1],
      bottom: volumeOrientationPlain[4],
      front: volumeOrientationPlain[2],
      back: volumeOrientationPlain[5]
    };

    data.orientationInfo = {
      canvas: data.renderingContext.canvas,
      xRaySlicerParams: xRaySlicerParams,
      sliceThickness: sliceThickness,
      slicesCount: slicesCount,
      volumeCS: volumeCS,
      volumeOrientation: volumeOrientation,
      volumeDimensions: sliceSizes.clone().multiply(sliceSpacings),
      voxelDimensions: sliceSpacings,
      imageReductionFactor: data.slicingContextRef.imageReductionFactor,
      isReduced: this._getImageStack().isReduced()
    };

    return data.orientationInfo;
  }

  private _buildPositionInfo(sliceType: SliceType): SlicePositionInfo {
    const data = this._data.get(sliceType);

    if (!data?.slicingContextRef || !data?.orientationInfo) {
      throw new Error('Slice orientationInfo in not existent for sliceType: ' + sliceType);
    }

    let sliceIndex = 0;
    let slicePosition = 0.0;
    const sliceCS = new THREE.Matrix4();
    if (data.slicingContextRef.imageSlice) {
      sliceIndex = data.slicingContextRef.imageSlice.getSliceIndex();
      slicePosition = data.slicingContextRef.imageSlice.getSlicePosition();
      sliceCS.copy(data.slicingContextRef.imageSlice.getSliceCS());
    }

    data.positionInfo = {
      xRayEffect: data.orientationInfo.xRaySlicerParams?.xRayEffect,
      sliceThickness: data.orientationInfo.sliceThickness,
      sliceIndex: sliceIndex,
      slicePosition: slicePosition,
      mprSliceRotations: this._getMprSliceRotations(sliceType),
      sliceCS: sliceCS,
      sliceCSInv: sliceCS.clone().invert()
    };

    return data.positionInfo;
  }

  private _buildVisualizationInfo(
    sliceType: SliceType,
    contrastWindowWidth = 0,
    contrastWindowLevel = 0
  ): SliceVisualizationInfo {
    const data = this._data.get(sliceType);

    if (!data?.slicingContextRef) {
      throw new Error('Slice slicerContext in not existent for sliceType: ' + sliceType);
    }

    const reductionFactorFromOriginal =
      data.slicingContextRef.imageReductionFactor * data.slicingContextRef.sliceReductionFactor;

    data.visualizationInfo = {
      reductionFactorFromOriginal: reductionFactorFromOriginal,
      contrastWindowWidth: contrastWindowWidth,
      contrastWindowLevel: contrastWindowLevel
    };

    return data.visualizationInfo;
  }

  private _onImageStackReady(): void {
    if (!this._getImageStack().isReady()) {
      console.warn('Image stack is not initialized yet');
      return;
    }

    this._orientationCSs = new Map();
    for (const sliceType of this._sliceTypes.keys()) {
      const orientationCS = this._getScanDefaultOrientationCS(sliceType, this._shouldCompensateObliqueness);
      this._orientationCSs.set(sliceType, orientationCS);
    }
    this._initialOrientationCSs = this._getInitialOrientationCSs();

    this._complimentarySlicesInfos = this._getComplimentarySlicesInfos();

    this._slicePositions = new Map();

    for (const sliceType of this._sliceTypes.keys()) {
      this._buildSlicingContext(sliceType, false);
      this._buildOrientationInfo(sliceType);
      this._buildPositionInfo(sliceType);
      this._buildVisualizationInfo(sliceType);
    }

    for (const sliceType of this._sliceTypes.keys()) {
      this._notifySliceOrientationChanged(sliceType);
    }

    this._slicesInitializer(this);

    this._updateSynchronizationPoint();

    this._initialSynchronizationPoint = this._synchronizationPoint?.clone();

    for (const sliceType of this._sliceTypes.keys()) {
      this._notifySlicePositionChanged(sliceType);
    }

    this._notifySynchronizationPointChanged();

    for (const sliceType of this._sliceTypes.keys()) {
      this._notifySliceVisualizationChanged(sliceType);
    }

    this._isReady = true;

    if (this._initializationJob) {
      this._initializationJob();
      this._initializationJob = undefined;
    }

    this._notifyReadyStateChanged(true);
  }

  private static _areSlicingContextsEqual(a?: SlicingContext, b?: SlicingContext): boolean {
    if (!a || !b) {
      return false;
    }
    if (Math.abs(a.imageReductionFactor - b.imageReductionFactor) > 1e-4) {
      return false;
    }
    // Let's check Size and Spacings,
    // as they could differ because of runtime reduction
    if (a.imageSlicer.getSize().distanceToSquared(b.imageSlicer.getSize()) > 1e-8) {
      return false;
    }
    if (a.imageSlicer.getSpacings().distanceToSquared(b.imageSlicer.getSpacings()) > 1e-8) {
      return false;
    }
    // Let's ignore Orientation and CS,
    // assuming they are 100% equal for Multi-Stack images
    return true;
  }

  private _onImageStackUpdated(isSameImageStack: boolean): void {
    const slicingContexts = this._getSlicingContexts();

    const preservedSlicingContexts = new Map<SliceType, SlicingContext>();
    if (!isSameImageStack) {
      for (const [sliceType, data] of this._data) {
        const slicingContext = slicingContexts.get(sliceType);
        const isContextPreserved = ImageStackController._areSlicingContextsEqual(
          data.slicingContextRef,
          slicingContext
        );
        if (isContextPreserved && slicingContext) {
          preservedSlicingContexts.set(sliceType, slicingContext);
        }
      }
    }

    const updatedImageSlices = new Set<SliceType>();
    for (const [sliceType, data] of this._data) {
      const newSlicingContext = preservedSlicingContexts.get(sliceType);
      if (!newSlicingContext) {
        this._buildSlicingContext(sliceType, false);
        this._buildOrientationInfo(sliceType);
      } else {
        data.slicingContextRef = newSlicingContext;
      }
      if (!data.slicingContextRef) {
        throw new Error('Slicing Context expected to be initialized here');
      }
      if (!data.slicingContextRef.imageSlice) {
        const pt = this._slicePositions?.get(sliceType);
        if (!pt) {
          throw new Error('Slice Position expected to be initialized here');
        }
        this._requestSlice(sliceType, pt, false);
        updatedImageSlices.add(sliceType);
      } else {
        this._applyContrast(sliceType);
      }
    }

    for (const [sliceType, data] of this._data) {
      if (!preservedSlicingContexts.has(sliceType)) {
        if (!data.orientationInfo) {
          throw new Error('Slice orientationInfo is invalid');
        }
        data.orientationInfo.deltaTransformation = new THREE.Matrix4().identity(); // identity but not undefined deltaTransformation is a trick to avoid camera reset
        this._notifySliceOrientationChanged(sliceType);
      }
    }
    for (const sliceType of this._sliceTypes.keys()) {
      if (updatedImageSlices.has(sliceType)) {
        this._notifySlicePositionChanged(sliceType);
      }
    }
    for (const sliceType of this._sliceTypes.keys()) {
      this._notifySliceVisualizationChanged(sliceType);
    }
    for (const data of this._data.values()) {
      if (data.orientationInfo?.deltaTransformation) {
        data.orientationInfo.deltaTransformation = undefined; // reset it back
      }
    }
  }

  private _isLowRamDevice(): boolean {
    return isMobile && !isAndroid;
  }

  private _areOriginalImagesSupported(imageStack: ImageStack): boolean {
    if (this._isLowRamDevice()) {
      const size = imageStack.getSize();
      const lqRamAmount = size.x * size.y * size.z * 2;
      const reductionFactor = imageStack.getReductionFactor() ?? 1.5;
      const hqRamAmount = lqRamAmount * reductionFactor * reductionFactor * reductionFactor;
      const ramAvailableMb = 512;
      return ramAvailableMb * 1024 * 1024 > ((lqRamAmount + hqRamAmount) * 4) / 3;
    } else {
      return true;
    }
  }

  private _isCineloopSupported(imageStack: ImageStack): boolean {
    if (this._isLowRamDevice()) {
      return false;
    } else {
      const size = imageStack.getSize();
      const hqRamAmount = size.x * size.y * size.z * imageStack.getComponentSize();
      const ramAvailableGb = 1.8;
      return ramAvailableGb * 1024 * 1024 * 1024 > hqRamAmount * this._medicalImage.ids.length;
    }
  }

  private async _loadImages(isReduced: boolean): Promise<void> {
    if (this._isReady) {
      throw new Error('Logic Error');
    }
    const imageStack = this._getImageStack();
    try {
      this._onLoadingProgressListeners.invoke(false, 0, 0);
      await imageStack.loadAsync(isReduced, (framesLoaded: number, framesTotal: number) => {
        this._onLoadingProgressListeners.invoke(isReduced, framesLoaded, framesTotal);
      });
      this._onImageStackReady();
      if (imageStack.isReduced() && this._areOriginalImagesSupported(imageStack)) {
        this._onLoadingProgressListeners.invoke(false, 0, 0);
        await imageStack.loadAsync(false);
        this._onImageStackUpdated(true);
        this._notificationsCenter?.onInfo('HQ Images have been loaded');
        eventBus.emit('testing.imageStack.loaded', null, 'HQ');
      } else {
        eventBus.emit('testing.imageStack.loaded', null, imageStack.isReduced() ? 'LQ' : 'HQ');
      }
      if (!imageStack.isReduced() && this._isMultiPhase && this._isCineloopSupported(imageStack)) {
        const remainingIds = this._medicalImage.ids.filter((value) => value !== this._activeImageStackId);
        for (let i = 0, cnt = remainingIds.length; i < cnt; ++i) {
          const id = remainingIds[i];
          const imageStack = this._imageStackContexts.get(id)?.imageStack;
          if (!imageStack) throw new Error('Logic Error');
          await imageStack.loadAsync(false);
        }
        this._isCineloopAvailable = true;
        this._notificationsCenter?.onInfo('Phases loading has completed');
        eventBus.emit('testing.imageStack.multiphaseLoaded', null);
      }
      this._isCompleted = true;
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.error('FAILED: ' + e.message);
        this._notifyError();
      }
      throw e;
    }
  }

  private _notifySliceOrientationChanged(sliceType: SliceType): void {
    const data = this._data.get(sliceType);
    if (!data?.orientationInfo) {
      throw new Error('Slice Data is invalid');
    }
    data.onOrientationChangedListeners.invoke(data.orientationInfo);
    eventBus.emit('testing.imageStack.sliceOrientationChanged', null, sliceType, data.orientationInfo);
  }

  private _notifySlicePositionChanged(sliceType: SliceType): void {
    const data = this._data.get(sliceType);
    if (!data?.positionInfo) {
      throw new Error('Slice Data is invalid');
    }
    data.onPositionChangedListeners.invoke(data.positionInfo);
    eventBus.emit('testing.imageStack.slicePositionChanged', null, sliceType, data.positionInfo);
  }

  private _notifySliceVisualizationChanged(sliceType: SliceType): void {
    const data = this._data.get(sliceType);
    if (!data?.visualizationInfo) {
      throw new Error('Slice Data is invalid');
    }
    data.onVisualizationChangedListeners.invoke(data.visualizationInfo);
    eventBus.emit('testing.imageStack.sliceVisualizationChanged', null, sliceType, data.visualizationInfo);
  }

  private _notifySynchronizationPointChanged(): void {
    if (!this._synchronizationPoint) {
      throw new Error('Slices are not synchronized');
    }
    this._onSynchronizationPointChangedListeners.invoke(this._synchronizationPoint);
    eventBus.emit('testing.imageStack.slicesSynchronized', null);
  }

  private _notifyReadyStateChanged(isReady: boolean): void {
    this._onReadyStateChangedListeners.invoke(isReady);
    eventBus.emit('testing.imageStack.readyStateChanged', null, isReady);
  }

  private _notifyError(): void {
    this._isError = true;
    this._onErrorListeners.invoke();
  }

  private _applyContrast(sliceType: SliceType): void {
    const data = this._data.get(sliceType);

    if (!data?.slicingContextRef?.imageSlice) {
      return;
    }

    data.slicingContextRef.imageSlice.render(data.renderingContext, this._contrastWindow);

    const contrastWindowWidth = this._contrastWindow.max - this._contrastWindow.min;
    let contrastWindowLevel =
      this._contrastWindow.min + Math.floor((this._contrastWindow.max - this._contrastWindow.min) / 2);

    if (this._getImageStack().getGVMinMax().max > 255) {
      contrastWindowLevel -= 1024; // for Hounsfield(HU) in case of > 8bit image data
    }

    this._buildVisualizationInfo(sliceType, contrastWindowWidth, contrastWindowLevel);
  }

  private _requestSlice(sliceType: SliceType, param: THREE.Vector3 | number, resetMultiStackContext = true): void {
    const data = this._data.get(sliceType);

    if (!data?.slicingContextRef) {
      console.warn('Slice slicerContext in not existent for sliceType: ', sliceType);
      return;
    }

    if (resetMultiStackContext) {
      for (const imageStackContext of this._imageStackContexts.values()) {
        const slicingContext = imageStackContext.slicingContexts.get(sliceType);
        if (slicingContext?.imageSlice) {
          slicingContext.imageSlice.dispose();
          slicingContext.imageSlice = undefined;
        }
      }
    } else {
      data.slicingContextRef.imageSlice?.dispose();
    }
    data.slicingContextRef.imageSlice = undefined;

    if (typeof param === 'number') {
      data.slicingContextRef.imageSlice = data.slicingContextRef.imageSlicer.getSliceByIndex(param);
    } else {
      data.slicingContextRef.imageSlice = data.slicingContextRef.imageSlicer.getSliceByCoplanarPoint(param);
    }

    const slicePosition = new THREE.Vector3().setFromMatrixPosition(data.slicingContextRef.imageSlice.getSliceCS());
    if (!this._slicePositions) {
      throw new Error('Slice Positions expected to be set so far.');
    }
    this._slicePositions.set(sliceType, slicePosition);

    this._buildPositionInfo(sliceType);

    this._applyContrast(sliceType);
  }

  private _updateSynchronizationPoint(): void {
    const pt = new THREE.Vector3(0, 0, 0);
    for (const sliceType of this._mainSliceTypes) {
      const toWorld = this._data.get(sliceType)?.positionInfo?.sliceCS;
      const fromWorld = this._data.get(sliceType)?.positionInfo?.sliceCSInv;
      if (toWorld && fromWorld) {
        pt.applyMatrix4(fromWorld);
        pt.z = 0;
        pt.applyMatrix4(toWorld);
      }
    }
    this._synchronizationPoint = pt;
  }

  //-------------------- Listeners ------------------------
  public addOnOrientationChangedListener(
    sliceType: SliceType,
    callback: OnOrientationChangedListener,
    immediateNotify = false
  ): CallbackSubscription {
    const data = this._data.get(sliceType);
    if (!data) {
      throw new Error('Slice Data is undefined');
    }
    return safeResource(data.onOrientationChangedListeners.subscribe(callback), () => {
      if (immediateNotify && data.orientationInfo) {
        callback(data.orientationInfo);
      }
    });
  }

  public addOnPositionChangedListener(
    sliceType: SliceType,
    callback: OnPositionChangedListener,
    immediateNotify = false
  ): CallbackSubscription {
    const data = this._data.get(sliceType);
    if (!data) {
      throw new Error('Slice Data is undefined');
    }
    return safeResource(data.onPositionChangedListeners.subscribe(callback), () => {
      if (immediateNotify && data.positionInfo) {
        callback(data.positionInfo);
      }
    });
  }

  public addOnVisualizationChangedListener(
    sliceType: SliceType,
    callback: OnVisualizationChangedListener,
    immediateNotify = false
  ): CallbackSubscription {
    const data = this._data.get(sliceType);
    if (!data) {
      throw new Error('Slice Data is undefined');
    }
    return safeResource(data.onVisualizationChangedListeners.subscribe(callback), () => {
      if (immediateNotify && data.visualizationInfo) {
        callback(data.visualizationInfo);
      }
    });
  }

  public addOnErrorOccurredListener(callback: OnErrorListener, immediateNotify = false): CallbackSubscription {
    return safeResource(this._onErrorListeners.subscribe(callback), () => {
      if (immediateNotify && this._isError) {
        callback();
      }
    });
  }

  public addOnSynchronizationPointChangedListener(
    callback: OnSynchronizationPointChangedListener,
    immediateNotify = false
  ): CallbackSubscription {
    return safeResource(this._onSynchronizationPointChangedListeners.subscribe(callback), () => {
      if (immediateNotify && this._synchronizationPoint) {
        callback(this._synchronizationPoint);
      }
    });
  }

  public addOnReadyStateChangedListener(
    callback: OnReadyStateChangedListener,
    immediateNotify = false
  ): CallbackSubscription {
    return safeResource(this._onReadyStateChangedListeners.subscribe(callback), () => {
      if (immediateNotify) {
        callback(this._isReady);
      }
    });
  }

  public addOnLoadingProgressListener(callback: OnLoadingProgressListener): CallbackSubscription {
    return this._onLoadingProgressListeners.subscribe(callback);
  }

  //------------------ Interface -------------

  public dispose(): void {
    this._initializationJob = undefined;
    for (const data of this._data.values()) {
      data.renderingContext.dispose();
      data.slicingContextRef = undefined;
      data.orientationInfo = undefined;
      data.positionInfo = undefined;
    }
    for (const val of this._imageStackContexts.values()) {
      val.imageStack.dispose();
      for (const slicingContext of val.slicingContexts.values()) {
        if (slicingContext.imageSlice) {
          slicingContext.imageSlice.dispose();
          slicingContext.imageSlice = undefined;
        }
        slicingContext.imageSlicer.dispose();
      }
      val.slicingContexts.clear();
    }
    this._activeImageStackId = undefined;
    this._isCompleted = false;
    this._isCineloopAvailable = false;
  }

  public async loadImagesAsync(id: string): Promise<void> {
    this._activeImageStackId = id;
    const imageStack = this._getImageStack();
    if (!imageStack.isLoadingIncomplete()) {
      if (!imageStack.isReady()) {
        await this._loadImages(true);
      } else {
        this._onImageStackReady();
        if (imageStack.isReady() && imageStack.isReduced() && this._areOriginalImagesSupported(imageStack)) {
          await this._loadImages(false);
          this._onLoadingProgressListeners.invoke(false, 0, 0);
        } else {
          for (const [, val] of this._imageStackContexts) {
            val.imageStack.resumeLoading();
          }
        }
      }
    } else {
      if (imageStack.isReady()) {
        this._onImageStackReady();
      }
      imageStack.resumeLoading();
    }
  }

  public async switchImagesAsync(id: string): Promise<void> {
    if (!this._isMultiPhase) {
      throw new Error('Switch is intended for MultiStack case');
    }
    if (!this._isCompleted) {
      throw new Error('Logic Error');
    }
    if (!this._isCineloopAvailable) {
      this._isReady = false;
      this._notifyReadyStateChanged(false);
      this._getImageStack().dispose();
      this._activeImageStackId = id;
      await this._loadImages(true);
    } else {
      this._activeImageStackId = id;
    }
    this._onImageStackUpdated(false);
  }

  public addXRayMesh(
    descriptor: string,
    bufferGeometry: THREE.BufferGeometry,
    contrastValue: number,
    tolerance: number
  ): void {
    this._getImageStack().addXRayMesh(descriptor, bufferGeometry, contrastValue, tolerance);
  }

  public addXRayMeshCS(
    descriptor: string,
    bufferGeometry: THREE.BufferGeometry,
    cs: THREE.Matrix4,
    contrastValue: number,
    tolerance: number
  ): void {
    this._getImageStack().addXRayMesh(descriptor, bufferGeometry.clone().applyMatrix4(cs), contrastValue, tolerance);
  }

  public unloadImages(): void {
    this._isReady = false;
    this._notifyReadyStateChanged(false);
    this.dispose();
  }

  public suspendImages(): void {
    if (this._isLowRamDevice()) {
      this.unloadImages();
    } else {
      this._notifyReadyStateChanged(false);
      for (const [, val] of this._imageStackContexts) {
        val.imageStack.suspendLoading();
      }
      this._onLoadingProgressListeners.invoke(false, 0, 0);
      this._initializationJob = undefined;
    }
  }

  public getRegisteredSliceTypes(): Array<SliceType> {
    return this._mainSliceTypes;
  }

  public getSliceIndexSetter(sliceType: SliceType): SliceIndexSetter {
    return (index: number): void => {
      this._requestSlice(sliceType, index);
      this._updateSynchronizationPoint();
      this._notifySlicePositionChanged(sliceType);
      this._notifySynchronizationPointChanged();
      this._notifySliceVisualizationChanged(sliceType);
    };
  }

  public getSliceByPointSetter(sliceType: SliceType): SliceByPointSetter {
    return (point: THREE.Vector3): void => {
      this._requestSlice(sliceType, point);
      this._updateSynchronizationPoint();
      this._notifySlicePositionChanged(sliceType);
      this._notifySynchronizationPointChanged();
      this._notifySliceVisualizationChanged(sliceType);
    };
  }

  public requestSliceByIndex(sliceType: SliceType, index: number): void {
    this._requestSlice(sliceType, index);
    this._updateSynchronizationPoint();
    this._notifySlicePositionChanged(sliceType);
    this._notifySynchronizationPointChanged();
    this._notifySliceVisualizationChanged(sliceType);
  }

  public requestSliceByPoint(sliceType: SliceType, point: THREE.Vector3): void {
    this._requestSlice(sliceType, point);
    this._updateSynchronizationPoint();
    this._notifySlicePositionChanged(sliceType);
    this._notifySynchronizationPointChanged();
    this._notifySliceVisualizationChanged(sliceType);
  }

  public synchronizeImages(point3D: THREE.Vector3): void {
    // We need to synchronize only "main" images
    for (const sliceType of this._mainSliceTypes) {
      const orientationInfo = this._data.get(sliceType)?.orientationInfo;
      if (orientationInfo) {
        this._requestSlice(sliceType, point3D);
      } else {
        console.warn('synchronizeImages is ignored because orientationInfo is not initialized yet');
      }
    }
    this._updateSynchronizationPoint();
    for (const sliceType of this._mainSliceTypes) {
      this._notifySlicePositionChanged(sliceType);
    }
    this._notifySynchronizationPointChanged();
    for (const sliceType of this._mainSliceTypes) {
      this._notifySliceVisualizationChanged(sliceType);
    }
  }

  public getSliceIndexByPoint3D(point3D: THREE.Vector3, sliceType: SliceType): number {
    const orientationInfo = this._data.get(sliceType)?.orientationInfo;
    if (!orientationInfo) {
      throw new Error('Slice orientationInfo is undefined');
    }
    const point = point3D.clone().applyMatrix4(new THREE.Matrix4().copy(orientationInfo.volumeCS).invert());
    const sliceIndex: number = THREE.MathUtils.clamp(
      Math.round(point.z / orientationInfo.voxelDimensions.z),
      0,
      orientationInfo.slicesCount - 1
    );
    return sliceIndex;
  }

  public getGVMinMax(): MinMax {
    return this._getImageStack().getGVMinMax();
  }

  public getContrast(): MinMax {
    return this._contrastWindow;
  }

  public setContrast(contrastWindow: MinMax): void {
    this._contrastWindow.min = contrastWindow.min;
    this._contrastWindow.max = contrastWindow.max;
    for (const sliceType of this._sliceTypes.keys()) {
      this._applyContrast(sliceType);
    }
    for (const sliceType of this._sliceTypes.keys()) {
      this._notifySliceVisualizationChanged(sliceType);
    }
  }

  public resetContrast(): void {
    this.setContrast(this._contrastWindowDef);
  }

  public getSlicesOrientationsMementos(useInitialOrientation = false): Map<SliceType, THREE.Matrix4> {
    const ret: Map<SliceType, THREE.Matrix4> = new Map();
    const orientations = useInitialOrientation ? this._initialOrientationCSs : this._orientationCSs;
    // For MPR we are interested only in "main" images
    for (const sliceType of this._mainSliceTypes) {
      const orientationCS = orientations?.get(sliceType);
      if (!orientationCS) {
        throw new Error('Slice Orientation CS is undefined');
      }
      ret.set(sliceType, orientationCS.clone());
    }
    return ret;
  }

  public setSlicesOrientationsMementos(
    orientationCSs: Map<SliceType, THREE.Matrix4>,
    origin?: THREE.Vector3,
    deltaTransformations?: Map<SliceType, THREE.Matrix4>
  ): void {
    if (!this._orientationCSs) {
      throw new Error('Orientation CSs expected to be set so far.');
    }
    const coplanarPoint = origin ?? this._synchronizationPoint;
    if (!coplanarPoint) {
      throw new Error('Slices expected to be initialized so far');
    }
    for (const [sliceType, orientationCS] of orientationCSs) {
      this._orientationCSs.set(sliceType, orientationCS.clone());
      this._buildSlicingContext(sliceType);
      this._buildOrientationInfo(sliceType);
      this._requestSlice(sliceType, coplanarPoint);
    }
    this._updateSynchronizationPoint();
    for (const sliceType of orientationCSs.keys()) {
      if (deltaTransformations) {
        const orientationInfo = this._data.get(sliceType)?.orientationInfo;
        if (!orientationInfo) {
          throw new Error('Slice orientationInfo is invalid');
        }
        orientationInfo.deltaTransformation = deltaTransformations.get(sliceType);
        if (!orientationInfo.deltaTransformation) {
          throw new Error('Invalid deltaTransformation');
        }
      }
      this._notifySliceOrientationChanged(sliceType);
    }
    for (const sliceType of orientationCSs.keys()) {
      this._notifySlicePositionChanged(sliceType);
    }
    this._notifySynchronizationPointChanged();
    for (const sliceType of this._sliceTypes.keys()) {
      this._notifySliceVisualizationChanged(sliceType);
    }
    if (deltaTransformations) {
      for (const sliceType of orientationCSs.keys()) {
        const orientationInfo = this._data.get(sliceType)?.orientationInfo;
        if (orientationInfo?.deltaTransformation) {
          orientationInfo.deltaTransformation = undefined; // reset it back
        }
      }
    }
  }

  public getSlicesIntersectionPoint(): THREE.Vector3 | undefined {
    return this._synchronizationPoint;
  }

  public applyMpr(
    slicesOrientationsMementos: Map<SliceType, THREE.Matrix4>,
    sliceType: SliceType,
    origin: THREE.Vector3,
    rotationMatrix?: THREE.Matrix4
  ): void {
    const orientationCSs: Map<SliceType, THREE.Matrix4> = new Map();
    const deltaTransformations: Map<SliceType, THREE.Matrix4> = new Map();
    // For MPR we are interested only in "main" images
    for (const _sliceType of this._mainSliceTypes) {
      if (_sliceType !== sliceType) {
        const orientationCS = slicesOrientationsMementos.get(_sliceType)?.clone();
        if (!orientationCS) {
          throw new Error('Slice Orientation CS is undefined');
        }
        if (rotationMatrix) {
          orientationCS.premultiply(rotationMatrix);
          const prevCS = this._orientationCSs?.get(_sliceType)?.clone();
          if (!prevCS || !this._synchronizationPoint) {
            throw new Error('Slice Orientation CS is undefined');
          }
          prevCS.setPosition(this._synchronizationPoint);
          const newCs = orientationCS.clone().setPosition(origin);
          deltaTransformations.set(_sliceType, prevCS.invert().premultiply(newCs));
        } else {
          deltaTransformations.set(_sliceType, new THREE.Matrix4().identity());
        }
        orientationCSs.set(_sliceType, orientationCS);
      }
    }
    this.setSlicesOrientationsMementos(orientationCSs, origin, deltaTransformations);
  }

  public resetMpr(isDeltaTransformation = false, resetPosition = false): void {
    const job = (): void => {
      if (!this._isReady) {
        this._initializationJob = undefined;
        return;
      }
      const origin = resetPosition ? this._initialSynchronizationPoint : this._synchronizationPoint;
      if (!this._initialOrientationCSs || !origin) {
        throw new Error('Default Orientation CSs are invalid');
      }
      const orientationCSs = this._initialOrientationCSs;
      if (isDeltaTransformation) {
        const deltaTransformations: Map<SliceType, THREE.Matrix4> = new Map();
        // For MPR we are interested only in "main" images
        for (const _sliceType of this._mainSliceTypes) {
          const prevCS = this._orientationCSs?.get(_sliceType)?.clone();
          if (!prevCS) {
            throw new Error('Slice Orientation CS is undefined');
          }
          prevCS.setPosition(origin);
          const newCs = orientationCSs.get(_sliceType)?.clone();
          if (!newCs) {
            throw new Error('Slice Orientation CS is undefined');
          }
          newCs.setPosition(origin);
          deltaTransformations.set(_sliceType, prevCS.invert().premultiply(newCs));
        }
        this.setSlicesOrientationsMementos(orientationCSs, origin, deltaTransformations);
      } else {
        this.setSlicesOrientationsMementos(orientationCSs, origin);
      }
    };
    if (this._isReady) {
      job();
    } else {
      this._initializationJob = job;
    }
  }

  public setMprSliceRotation(sliceType: SliceType, complimentarySliceType: SliceType, rotationAngleRad: number): void {
    const slicePositionInfo = this.getSlicePositionInfo(sliceType);

    if (!slicePositionInfo) {
      throw new Error(`${sliceType} position info is not initialized`);
    }

    const origin = this.getSlicesIntersectionPoint();
    const currentAngleRad = slicePositionInfo.mprSliceRotations.get(complimentarySliceType);

    if (!origin || currentAngleRad === undefined) {
      throw new Error('Not initialized');
    }

    const orientationMementos = this.getSlicesOrientationsMementos();

    const cs = orientationMementos.get(complimentarySliceType);
    if (!cs) {
      throw new Error('Invalid data');
    }

    const _rotationMatrix = makeRotationMatrix(
      origin,
      new THREE.Vector3().setFromMatrixColumn(cs, 2),
      currentAngleRad - rotationAngleRad
    );

    this.applyMpr(orientationMementos, complimentarySliceType, origin, _rotationMatrix);
  }

  private _getInitialOrientationCSs(): Map<SliceType, THREE.Matrix4> {
    const orientationCSs: Map<SliceType, THREE.Matrix4> = new Map();
    for (const _sliceType of this._mainSliceTypes) {
      const orientationCS = this._orientationCSs?.get(_sliceType)?.clone();
      if (!orientationCS) {
        throw new Error('Slice Orientation CS is undefined');
      }
      orientationCSs.set(_sliceType, orientationCS);
    }
    return orientationCSs;
  }

  public setCustomSliceOrientation(sliceType: SliceType, orientationCS: THREE.Matrix4, origin: THREE.Vector3): void {
    const isDefault = this._sliceTypes.get(sliceType);
    if (isDefault === undefined) {
      throw new Error('Slice Type is not initialized');
    }
    if (isDefault) {
      throw new Error('Orientation set for Main slice is not allowed.');
    }
    if (!this._orientationCSs) {
      throw new Error('Orientation CSs expected to be set so far.');
    }
    this._orientationCSs.set(sliceType, orientationCS.clone());
    this._buildSlicingContext(sliceType);
    this._buildOrientationInfo(sliceType);
    this._notifySliceOrientationChanged(sliceType);
    this._requestSlice(sliceType, origin);
    this._notifySlicePositionChanged(sliceType);
    this._notifySliceVisualizationChanged(sliceType);
  }

  public resolveClosestOrientation(
    vectorZ: THREE.Vector3,
    useInitialOrientation = false
  ): {
    sliceType: SliceType;
    adjustedVectorZ: THREE.Vector3;
    adjustedVectorY?: THREE.Vector3;
  } {
    const orientationCSs = useInitialOrientation ? this._initialOrientationCSs : this._orientationCSs;
    if (!orientationCSs) {
      throw new Error('Orientation CSs are invalid');
    }

    let minVecZ: THREE.Vector3 | undefined = undefined;
    let minVecY: THREE.Vector3 | undefined = undefined;
    let sliceType = SliceType.NONE;
    let minAngle = Math.PI * 2;
    for (const _sliceType of this._mainSliceTypes) {
      const cs = orientationCSs.get(_sliceType);
      if (cs) {
        const vecZ = new THREE.Vector3();
        const vecY = new THREE.Vector3();
        cs.extractBasis(new THREE.Vector3(), vecY, vecZ);
        let angle = vecZ.angleTo(vectorZ);
        if (angle > Math.PI / 2) angle = angle - Math.PI;
        angle = Math.abs(angle);
        if (angle < minAngle) {
          minAngle = angle;
          minVecZ = vecZ;
          minVecY = vecY;
          sliceType = _sliceType;
        }
      }
    }

    const adjustedVectorZ = vectorZ.clone().normalize();
    if (minVecZ && minVecZ.dot(adjustedVectorZ) < 0) {
      adjustedVectorZ.negate();
    }

    let adjustedVectorY: THREE.Vector3 | undefined = undefined;
    if (minVecZ && minVecY) {
      const quaternion = new THREE.Quaternion().setFromUnitVectors(minVecZ, adjustedVectorZ);
      adjustedVectorY = minVecY.clone().applyQuaternion(quaternion);
    }

    return { sliceType: sliceType, adjustedVectorZ: adjustedVectorZ, adjustedVectorY: adjustedVectorY };
  }

  public alignWithPlane(
    desiredSliceType: SliceType,
    origin: THREE.Vector3,
    vectorZ: THREE.Vector3,
    vectorY?: THREE.Vector3,
    excludedSlice?: SliceType,
    isDeltaTransformation = false,
    shouldCentrify = true
  ): void {
    const job = (): void => {
      if (!this._initialOrientationCSs) {
        throw new Error('Default Orientation CSs are invalid');
      }
      const defaultOrientationCSs = this._initialOrientationCSs;

      let sliceType = desiredSliceType;
      let adjustedVectorZ = vectorZ;
      if (desiredSliceType == SliceType.NONE) {
        const resolved = this.resolveClosestOrientation(vectorZ);
        sliceType = resolved.sliceType;
        adjustedVectorZ = resolved.adjustedVectorZ;
      }

      const sliceOrientationCS = defaultOrientationCSs.get(sliceType)?.clone();
      if (!sliceOrientationCS) {
        throw new Error('Slice Orientation CS is invalid');
      }
      const basisZ = new THREE.Vector3();
      const basisY = new THREE.Vector3();
      sliceOrientationCS.extractBasis(new THREE.Vector3(), basisY, basisZ);

      const vecZFrom = basisZ.clone().normalize();
      const vecZTo = adjustedVectorZ.clone().normalize();
      const align = new THREE.Quaternion().setFromUnitVectors(vecZFrom, vecZTo);

      const vecYFrom = basisY.clone().applyQuaternion(align).normalize();
      const vecYTo = vectorY
        ? vectorY.clone().cross(adjustedVectorZ).negate().cross(adjustedVectorZ).normalize()
        : adjustedVectorZ.clone().cross(basisY).cross(adjustedVectorZ).normalize();
      const alignY = new THREE.Quaternion().setFromUnitVectors(vecYFrom, vecYTo);
      align.premultiply(alignY);

      const rotationMatrix = new THREE.Matrix4().makeRotationFromQuaternion(align);

      const orientationCSs: Map<SliceType, THREE.Matrix4> = new Map();
      const deltaTransformations: Map<SliceType, THREE.Matrix4> = new Map();
      // For MPR we are interested only in "main" images
      for (const _sliceType of this._mainSliceTypes) {
        if (!excludedSlice || _sliceType !== excludedSlice) {
          const orientationCS = defaultOrientationCSs.get(_sliceType)?.clone();
          if (!orientationCS) {
            throw new Error('Slice Orientation CS is invalid');
          }
          orientationCS.premultiply(rotationMatrix);
          if (isDeltaTransformation) {
            const prevCS = this._orientationCSs?.get(_sliceType)?.clone();
            if (!prevCS || !this._synchronizationPoint) {
              throw new Error('Slice Orientation CS is invalid');
            }
            prevCS.setPosition(this._synchronizationPoint);
            const newCs = orientationCS.clone().setPosition(shouldCentrify ? origin : this._synchronizationPoint);
            deltaTransformations.set(_sliceType, prevCS.invert().premultiply(newCs));
          }
          orientationCSs.set(_sliceType, orientationCS);
        }
      }
      this.setSlicesOrientationsMementos(
        orientationCSs,
        origin,
        isDeltaTransformation ? deltaTransformations : undefined
      );
    };
    if (this._isReady) {
      job();
    } else {
      this._initializationJob = job;
    }
  }

  public setXRaySlicerParams(sliceType: SliceType, xRaySlicerParams?: XRaySlicerParams): void {
    if (xRaySlicerParams) {
      this._xRaySlicerParams.set(sliceType, xRaySlicerParams);
    } else {
      if (this._xRaySlicerParams.has(sliceType)) {
        this._xRaySlicerParams.delete(sliceType);
      } else {
        return;
      }
    }
    if (this._isReady) {
      this._onImageStackUpdated(true);
    }
  }

  public getXRaySlicerParams(sliceType: SliceType): XRaySlicerParams | undefined {
    return this._xRaySlicerParams.get(sliceType);
  }

  public getSliceOrientationInfo(sliceType: SliceType): SliceOrientationInfo | undefined {
    return this._data.get(sliceType)?.orientationInfo;
  }

  public getSlicePositionInfo(sliceType: SliceType): SlicePositionInfo | undefined {
    return this._data.get(sliceType)?.positionInfo;
  }

  public getSliceVisualizationInfo(sliceType: SliceType): SliceVisualizationInfo | undefined {
    return this._data.get(sliceType)?.visualizationInfo;
  }

  public getMedicalImage(): MedicalImage {
    return this._medicalImage;
  }

  public isReady(): boolean {
    return this._isReady;
  }

  public isCompleted(): boolean {
    return this._isCompleted;
  }

  public isCineloopAvailable(): boolean {
    return this._isCineloopAvailable;
  }
}
